import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, Loader2, Package } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useLang } from "@/context/LangContext";
import { analyzeCheckout, bundleCheckout } from "@/lib/api";

const useRedirect = (fn) => {
  const { tr } = useLang();
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      window.location.href = (await fn()).checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Errore pagamento", "Payment error"));
      setBusy(false);
    }
  };
  return [busy, go];
};

const BundleOffer = ({ bundle, loggedIn }) => {
  const { tr } = useLang();
  const [busy, buy] = useRedirect(bundleCheckout);
  const unit = (bundle.price / bundle.scans).toFixed(0);
  return (
    <div data-testid="bundle-offer" className="border-2 border-[#09090b] p-4">
      <div className="flex items-baseline justify-between gap-3">
        <div className="font-display text-lg inline-flex items-center gap-2"><Package className="w-4 h-4 text-[#E0262C]" /> {bundle.scans} {tr("analisi", "analyses")}</div>
        <div className="font-mono text-sm"><b>CHF {bundle.price}</b> <span className="text-[#71717a]">· CHF {unit} {tr("cad.", "each")}</span></div>
      </div>
      <p className="mt-1 text-xs text-[#5b6168]">{tr("Credito sul tuo account, valido 12 mesi. Si usa da solo quando l'analisi gratuita del mese è finita.", "Credit on your account, valid 12 months. Used automatically once the monthly free analysis is gone.")}</p>
      {loggedIn ? (
        <button type="button" data-testid="bundle-buy" onClick={buy} disabled={busy}
          className="mt-3 w-full h-11 bg-[#09090b] text-white font-medium inline-flex items-center justify-center gap-2 hover:bg-[#E0262C] transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />} {tr(`Compra ${bundle.scans} analisi a CHF ${bundle.price}`, `Buy ${bundle.scans} analyses for CHF ${bundle.price}`)}
        </button>
      ) : (
        <Link to="/login" data-testid="bundle-login" className="mt-3 w-full h-11 border border-[#09090b] font-medium inline-flex items-center justify-center hover:bg-[#09090b] hover:text-white transition-colors">
          {tr("Accedi o registrati per acquistarle", "Sign in or register to buy them")}
        </Link>
      )}
    </div>
  );
};

export const PaidScanDialog = ({ info, quota, url, lang, onClose }) => {
  const { tr } = useLang();
  const [busy, pay] = useRedirect(() => analyzeCheckout(url, lang));
  const next = info?.next_free_at && new Date(info.next_free_at).toLocaleDateString();
  return (
    <Dialog open={!!info} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="paid-scan-dialog" className="max-w-md bg-white">
        <DialogTitle className="font-display text-xl">{tr("Analisi gratuita già usata", "Free analysis already used")}</DialogTitle>
        <p className="text-sm text-[#5b6168]">
          {tr("Ogni mese hai 1 analisi gratuita. Le analisi successive costano CHF 5.", "You get 1 free analysis each month. Further analyses cost CHF 5.")}
          {next && <> {tr("La prossima gratuita sarà disponibile dal", "Your next free one is available from")} <b>{next}</b>.</>}
        </p>
        <div className="font-mono text-xs bg-[#f4f6f7] p-2 break-all">{url}</div>
        <button type="button" data-testid="paid-scan-pay" onClick={pay} disabled={busy}
          className="h-12 bg-[#E0262C] text-white font-medium inline-flex items-center justify-center gap-2 hover:bg-[#2B3036] transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} {tr("Paga CHF 5 e analizza", "Pay CHF 5 and analyse")}
        </button>
        {quota?.bundle && <BundleOffer bundle={quota.bundle} loggedIn={quota.logged_in} />}
      </DialogContent>
    </Dialog>
  );
};
